/**
 * cartUtils.js
 *
 * Pure utility functions for cart operations.
 * No React or external dependencies — safe to import anywhere.
 *
 * CANONICAL RULE:
 *   Every cart item in frontend state MUST have a valid string `id`.
 *   Backend field `productId` is NEVER used here — normalisation happens
 *   exclusively in src/services/userService.js (mapToBackend / mapFromBackend).
 */

import { logCartEvent } from './cartLogger';

// ── Validation ────────────────────────────────────────────────────────────────

/**
 * Returns true if the item is a valid frontend cart item.
 * A valid item MUST have a non-empty `id` that is either a string or a number
 * (products.js stores numeric ids; we accept both and coerce downstream).
 */
export function validateCartItem(item) {
  if (item == null) return false;
  const id = item.id;
  if (typeof id === 'string') return id.trim().length > 0;
  if (typeof id === 'number') return !isNaN(id);
  return false;
}

/**
 * Filters an array to only valid cart items.
 * Also coerces numeric `id` values to strings so that all downstream code
 * (validateCartItem, Map keys, backend mapToBackend) always receives a string id.
 *
 * Issue 4 fix: splits items into valid/invalid explicitly and uses console.error
 * (not warn) so invalid items are impossible to miss in dev tools. The dropped
 * items are logged in full so developers can inspect the bad data immediately.
 */
export function sanitizeCart(items) {
  if (!Array.isArray(items)) return [];

  const valid = [];
  const invalid = [];

  items.forEach((item) => {
    if (validateCartItem(item)) {
      // Coerce numeric id → string so the whole system uses string ids uniformly
      valid.push(typeof item.id === 'number' ? { ...item, id: String(item.id) } : item);
    } else {
      invalid.push(item);
    }
  });

  if (invalid.length > 0) {
    logCartEvent('invalid_items_detected', {
      droppedCount: invalid.length,
      items: invalid,
    });
  }

  return valid;
}

// ── Merge ─────────────────────────────────────────────────────────────────────

/**
 * Merges two arrays of cart items (local + DB) into one deduplicated list.
 *
 * Rules:
 *   - Items are keyed by `id`.
 *   - When the same id appears in both arrays, quantities are ADDED together
 *     and the richer local fields (image, element) are preserved.
 *   - Order: local items are processed first; DB items then update/add on top.
 *   - Invalid items (no `id`) are silently ignored.
 *
 * Guarantees:
 *   ✅ No duplicates possible — Map keyed on id
 *   ✅ Quantities merged (not overwritten)
 *   ✅ Order independent
 *   ✅ Safe with empty arrays
 */
export function mergeCarts(localItems, dbItems) {
  const map = new Map();

  // Process local items first so their richer data (image, element) is the base
  sanitizeCart(localItems).forEach((item) => {
    map.set(item.id, { ...item });
  });

  // Overlay DB items — DB wins on quantity (authoritative), but local image/element preserved
  sanitizeCart(dbItems).forEach((item) => {
    if (map.has(item.id)) {
      const existing = map.get(item.id);
      map.set(item.id, {
        ...item,                                  // DB data wins (quantity, price, name)
        image:   existing.image   || item.image,  // prefer richer local image
        element: existing.element || item.element,
      });
    } else {
      map.set(item.id, { ...item });
    }
  });

  return Array.from(map.values());
}

// ── Dev-mode assertions ───────────────────────────────────────────────────────

/**
 * In development, logs an error if duplicate ids are detected.
 * No-op in production.
 */
export function assertNoDuplicates(items, context = '') {
  if (process.env.NODE_ENV === 'production') return;
  const ids = items.map((i) => i.id);
  if (new Set(ids).size !== ids.length) {
    console.error(
      `🚨 [CartUtils] Duplicate cart items detected${context ? ` (${context})` : ''}.`,
      ids
    );
  }
}
